import * as React from 'react'
import { createRef, FunctionComponent, ReactElement, useEffect, useState } from 'react'
import { DetailsHeader, IColumn, IDetailsList, IDetailsListProps } from '@fluentui/react'
import { formatIsoDate } from '../../../helpers/dateHelper'
import { IFileDto } from '../../../config/fileConfig'
import { personaColumn, shortDateColumn } from '../../../helpers/detailsListHelper'
import { RegistrationMutationDto, RegistrationOverviewDto } from '../../../config/registrationConfig'
import { ViewRegistrationRowProps } from './ViewRegistrationRow'
import { ModifyRegistrationRowProps } from './ModifyRegistrationRow'
import { FormikHelpers } from 'formik'
import BaseDetailsList from '../../../components/List/BaseDetailsList'
import useUser from '../../../hooks/useUser'
import useAxios from '../../../hooks/useAxios'

export type RegistrationItemProps<I> = I & {
    file: IFileDto
}

export type RegistrationListProps<D extends RegistrationOverviewDto, I = unknown> = {
    listProps: RegistrationItemProps<I>
    items: D[]
    reset: () => any
}

type Props<D extends RegistrationOverviewDto, M extends RegistrationMutationDto, I> = RegistrationListProps<D, I> & {
    baseUrl: string
    initialValues: Omit<M, keyof RegistrationMutationDto> | Partial<RegistrationMutationDto>
    mapItem: (item: D) => Omit<M, keyof RegistrationMutationDto> | Partial<RegistrationMutationDto>
    viewRow: FunctionComponent<ViewRegistrationRowProps<D, I>>
    modifyRow: FunctionComponent<ModifyRegistrationRowProps<D, M, I>>
    detailsListProps: Omit<IDetailsListProps, 'items' | 'onItemInvoked' | 'onRenderRow' | 'onRenderDetailsFooter' | 'componentRef'>
    validationSchema?: any
}

type ColumnKey = 'user' | 'date' | 'description' | 'price' | 'invoice' | 'actions'

export const registrationListColumns: { [key in ColumnKey]: (...args) => IColumn } = {
    user: () =>
        personaColumn({
            key: 'user',
            minWidth: 70,
            maxWidth: 70,
            onRender: undefined,
        }),
    date: () =>
        shortDateColumn({
            key: 'date',
            fieldName: 'date',
            isSortedDescending: false,
            isSorted: true,
            minWidth: 48,
            maxWidth: 48,
            onRender: undefined,
        }),
    description: (translate: (key: string) => string) => ({
        key: 'description',
        fieldName: 'description',
        name: translate('base.table.header.description'),
        minWidth: 100,
        isResizable: true,
        isRowHeader: true,
        isSortable: true,
    }),
    price: (key: string, name: string, isCollapsible: boolean) => ({
        key,
        name,
        isCollapsible,
        fieldName: key,
        minWidth: 70,
        maxWidth: 70,
        isSortable: true,
    }),
    invoice: (translate: (key: string) => string) => ({
        key: 'invoice',
        name: translate('base.table.header.invoice'),
        minWidth: 80,
        maxWidth: 80,
        isCollapsible: true,
    }),
    actions: () => ({
        key: 'actions',
        name: '',
        minWidth: 24,
        maxWidth: 24,
        isResizable: false,
        className: 'ms-textAlignRight actions',
    }),
}

export default function RegistrationList<D extends RegistrationOverviewDto, M extends RegistrationMutationDto, I = unknown>(
    props: Props<D, M, I>,
): ReactElement {
    const axios = useAxios()
    const { currentUser } = useUser()
    const [activeItemId, setActiveItemId] = useState<string>()
    const ref = createRef<IDetailsList>()

    useEffect(() => {
        ref.current?.forceUpdate()
    }, [activeItemId])

    return (
        <BaseDetailsList
            {...props.detailsListProps}
            componentRef={ref}
            items={props.items || []}
            onItemInvoked={(item: D) => !item.invoice && setActiveItemId(item.id)}
            onRenderRow={rowProps => {
                const item = rowProps.item as D
                return activeItemId && item?.id === activeItemId ? (
                    <props.modifyRow
                        listProps={props.listProps}
                        rowProps={rowProps}
                        formikProps={{
                            initialValues: {
                                fileId: item.file.id,
                                userId: item.user.id,
                                date: item.date,
                                description: item.description,
                                rate: item.rate.amount,
                                currency: item.rate.currency,
                                ...props.mapItem(item),
                            } as M,
                            validationSchema: props.validationSchema || undefined,
                            onSubmit: (values: M) => {
                                setActiveItemId(undefined)
                                axios.put(`${props.baseUrl}/${item.id}`, values).then(props.reset)
                            },
                        }}
                    />
                ) : (
                    <props.viewRow
                        listProps={props.listProps}
                        rowProps={rowProps}
                        onDelete={() => {
                            setActiveItemId(undefined)
                            axios.delete(`${props.baseUrl}/${item?.id}`).then(props.reset)
                        }}
                    />
                )
            }}
            onRenderDetailsHeader={headerProps => (
                <>
                    <DetailsHeader {...headerProps} />
                    <props.modifyRow
                        listProps={props.listProps}
                        rowProps={{ ...(headerProps as any), item: { user: currentUser }, itemIndex: -1 }}
                        formikProps={{
                            initialValues: {
                                fileId: props.listProps.file.id,
                                userId: currentUser?.id,
                                date: formatIsoDate(new Date()),
                                description: '',
                                rate: '0.00',
                                currency: props.listProps.file.currency || 'EUR',
                                ...props.initialValues,
                            } as M,
                            validationSchema: props.validationSchema || undefined,
                            onSubmit: (values: M, formikHelpers: FormikHelpers<M>) => {
                                axios.post(props.baseUrl, values).then(() => {
                                    props.reset()
                                    formikHelpers.resetForm()
                                })
                                setActiveItemId(undefined)
                            },
                        }}
                    />
                </>
            )}
        />
    )
}
